/**
 * The single place an `AvatarConfig` becomes avatar artwork URLs.
 *
 * An avatar is two stacked layers of pre-rendered art (APP_SPEC §13.1): the
 * body — which carries the base, skin tone and eye shape — and the hair on top
 * of it. Both are authored on the same square canvas and shipped at the sizes
 * in `AVATAR_ASSET_SIZES`, so they composite by simply overlapping.
 *
 * **Both layers vary with `base`.** The bodies do not share a skull, so a
 * hairstyle is drawn once per body: its crown shape, forehead exposure and
 * silhouette are fitted to that head by hand, in the artwork. The renderer
 * therefore does nothing but stack two images — it never moves or scales one
 * layer to fit the other. What a hairstyle *is* (its part, its length, its
 * strand work) is constant across bodies and across every colour; only the
 * fit differs.
 *
 * Pipeline (see `assets-src/avatar/README.md`):
 *
 *   assets-src/avatar/masters/**            hand-drawn masters, one per body
 *     -> scripts/avatar-variants.py         every colour variant, 640x640
 *   assets-src/avatar/ready/**
 *     -> scripts/build-avatar-assets.mjs    downscaled into the shipped sizes
 *   apps/web/public/assets/shared/avatar/<size>/**
 *
 * No other module may build one of these paths. `avatar-assets.test.ts`
 * asserts every value of every enum resolves to a file that actually exists,
 * and that `BASE_ART` below matches the files on disk in both directions.
 */
import { AVATAR_HAIR_STYLES } from '@perplexicon/validation';
import type {
  AvatarBase,
  AvatarConfig,
  AvatarEyeShape,
  AvatarHairStyle,
} from '@perplexicon/validation';

/** Widths emitted by `scripts/build-avatar-assets.mjs`, smallest first. */
export const AVATAR_ASSET_SIZES = [128, 256] as const;

export type AvatarAssetSize = (typeof AVATAR_ASSET_SIZES)[number];

/** Public path prefix; matches `OUTPUT_DIR` in the build script. */
export const AVATAR_ASSET_ROOT = '/assets/shared/avatar';

/**
 * The eye shape every base is drawn in. A config naming a shape its base has
 * no drawing for renders this one instead, and it is the only shape the editor
 * offers until every base has the others.
 */
export const UNIVERSAL_EYE_SHAPE: AvatarEyeShape = 'almond';

interface BaseArt {
  /** Eye shapes this body has a master for. Always includes `UNIVERSAL_EYE_SHAPE`. */
  eyeShapes: readonly AvatarEyeShape[];
  /** Whether every hair style has been fitted to this body yet. */
  hair: boolean;
}

const ORIGINAL_BODY: BaseArt = { eyeShapes: ['round', 'almond', 'hooded', 'narrow'], hair: true };
const NEW_BODY: BaseArt = { eyeShapes: ['almond'], hair: true };

/**
 * Which artwork each body actually has. Bodies 2, 4, 5 and 7-10 were drawn in
 * almond only; every body has every hair style fitted to it. A body added
 * before its hair is drawn sets `hair: false`, and its hair layer is simply not
 * drawn until the art lands. When new masters land, update the entry here —
 * `avatar-assets.test.ts` fails in either direction if this table and the
 * shipped files disagree.
 */
const BASE_ART: Readonly<Record<AvatarBase, BaseArt>> = {
  'base-01': ORIGINAL_BODY,
  'base-02': NEW_BODY,
  'base-03': ORIGINAL_BODY,
  'base-04': NEW_BODY,
  'base-05': NEW_BODY,
  'base-06': ORIGINAL_BODY,
  'base-07': NEW_BODY,
  'base-08': NEW_BODY,
  'base-09': NEW_BODY,
  'base-10': NEW_BODY,
};

/** The eye shapes `base` is drawn in. */
export const drawnEyeShapes = (base: AvatarBase): readonly AvatarEyeShape[] =>
  BASE_ART[base].eyeShapes;

/** Whether `base` has hair art to draw over it. */
export const hasHairArt = (base: AvatarBase): boolean => BASE_ART[base].hair;

/** The hair style that is no hair: it has no artwork on any body and draws no layer. */
export const NO_HAIR_STYLE: AvatarHairStyle = 'bald';

/** The hair styles that have artwork — every style except `NO_HAIR_STYLE`. */
export const DRAWN_HAIR_STYLES: readonly AvatarHairStyle[] = AVATAR_HAIR_STYLES.filter(
  (style) => style !== NO_HAIR_STYLE,
);

/** Whether `config` puts a hair layer over the body. */
export const drawsHair = (config: AvatarConfig): boolean =>
  config.hairStyle !== NO_HAIR_STYLE && hasHairArt(config.base);

export type AvatarLayerKind = 'body' | 'hair';

/**
 * Path of one layer at one shipped size, relative to the web root. The base id
 * is the artwork directory. A body whose base is not drawn in the configured
 * eye shape resolves to `UNIVERSAL_EYE_SHAPE`, so a saved shape never points at
 * a missing file. A hair path is returned whether or not the base has hair art
 * yet; `avatarLayers` is what decides to draw it.
 */
export function avatarLayerUrl(
  kind: AvatarLayerKind,
  config: AvatarConfig,
  size: AvatarAssetSize,
): string {
  const { base } = config;
  if (kind === 'body') {
    const eyeShape = drawnEyeShapes(base).includes(config.eyeShape)
      ? config.eyeShape
      : UNIVERSAL_EYE_SHAPE;
    return `${AVATAR_ASSET_ROOT}/${size}/bases/${base}/${config.skinTone}/${eyeShape}.png`;
  }
  return `${AVATAR_ASSET_ROOT}/${size}/hair/${config.hairStyle}/${base}/${config.hairColor}.png`;
}

export interface AvatarLayerSource {
  src: string;
  /** `w`-descriptor candidates; pair with a `sizes` of the rendered CSS width. */
  srcSet: string;
}

/**
 * Both shipped sizes of one layer as an `<img>` `src`/`srcSet` pair. The
 * smallest size is the `src` fallback; the browser picks from `srcSet` using
 * the element's rendered width and the device pixel ratio.
 */
export function avatarLayerSource(kind: AvatarLayerKind, config: AvatarConfig): AvatarLayerSource {
  return {
    src: avatarLayerUrl(kind, config, AVATAR_ASSET_SIZES[0]),
    srcSet: AVATAR_ASSET_SIZES.map((size) => `${avatarLayerUrl(kind, config, size)} ${size}w`).join(
      ', ',
    ),
  };
}

/**
 * The layers to stack for `config`, bottom first: the body, then hair where it
 * is drawn (`drawsHair` — not for `NO_HAIR_STYLE`, nor a body without hair art).
 */
export function avatarLayers(config: AvatarConfig): AvatarLayerSource[] {
  const layers = [avatarLayerSource('body', config)];
  if (drawsHair(config)) layers.push(avatarLayerSource('hair', config));
  return layers;
}
